import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../data/company';
import { fetchCmsPageBySlug, fetchCmsGalleryBySlug } from '../lib/cms';
import ContactFormSection from '../components/ContactFormSection';
import LightboxModal from '../components/LightboxModal';

export default function AboutPage() {
  const [pageData, setPageData] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    async function loadAboutData() {
      const page = await fetchCmsPageBySlug('hvem-er-vi');
      const gal = await fetchCmsGalleryBySlug('hvem-er-vi');
      setPageData(page);
      setGallery(gal);
    }
    loadAboutData();
  }, []);

  const galleryImages = gallery?.images || [
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-gruppebillede-af-medarbejdere-01.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-gruppebillede-af-medarbejdere-02.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-firmabiler-og-servicekoeretoejer-01.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-firmabiler-og-servicekoeretoejer-02.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-firmabil-foran-virksomhed-01.jpg",
    "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-firmabil-foran-virksomhed-02.jpg"
  ];

  return (
    <>
      <Helmet>
        <title>{pageData?.meta_title || "Om os - OP Rengøringsservice"}</title>
        <meta name="description" content={pageData?.meta_description || "Læs mere om OP Rengøringsservice i Nibe. Vi er mere end 20 kompetente medarbejdere med fokus på kvalitet og tryghed."} />
        <meta property="og:title" content="Om os - OP Rengøringsservice" />
        <meta property="og:description" content="Hvem er vi? OP Rengøringsservice i Nibe." />
        <link rel="canonical" href="https://opservice.dk/hvem-er-vi" />
      </Helmet>

      {/* Header Banner */}
      <section className="bg-slate-900 text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-3xl">
          <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 inline-block mb-3">
            Om OP RENGØRINGSSERVICE
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4">
            Hvem er vi?
          </h1>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
            Mød OP Rengøringsservice – din lokale og pålidelige rengøringspartner.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Featured image at start of container */}
          <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-slate-100 aspect-[16/9] sm:aspect-[21/9] mb-12 max-h-[460px]">
            <img
              src={pageData?.hero_image?.[0] || "https://assets.auxodev.dk/assets/2c387ddb-b88b-45a9-8d4d-8c9017ed8767/andet-gruppebillede-af-medarbejdere-01.jpg"}
              alt="OP Rengøringsservice Hvem er vi"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-8 space-y-6">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Hvem er vi?
              </h2>

              <div className="prose-custom">
                {pageData?.body_text ? (
                  <div dangerouslySetInnerHTML={{ __html: pageData.body_text }} />
                ) : (
                  <div className="space-y-4 text-slate-700 leading-relaxed font-normal">
                    <p>
                      OP Rengøringsservice er en veletableret og erfaren rengøringsvirksomhed med base i Nibe. Vi leverer professionelle løsninger inden for privat- og erhvervsrengøring, vinduespolering, haveservice samt snerydning.
                    </p>
                    <p>
                      Med mere end 20 engagerede og kompetente medarbejdere lægger vi stor vægt på høj kvalitet, gensidig tillid og stabil service. Vores mål er altid at levere et pletfrit resultat, hvor du som kunde kan føle dig i helt trygge hænder.
                    </p>
                    <p>
                      Hvad enten du ønsker en fast rengøringsaftale til dit private hjem eller din virksomhed, eller har brug for periodisk vedligeholdelse af udendørsarealer, skræddersyr vi opgaven nøjagtigt efter dine behov.
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-sky-800 hover:bg-sky-900 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-sm"
                >
                  <span>Kontakt os i dag</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <Link
                  to="/ydelser"
                  className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 px-6 rounded-xl transition-colors"
                >
                  <span>Se vores ydelser</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-6">
              <div className="p-6 bg-sky-50 rounded-2xl border border-sky-100 space-y-3">
                <h3 className="font-bold text-sky-900 text-base flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-sky-600" aria-hidden="true" />
                  <span>Nøgletal & Værdier</span>
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-500 text-xs" aria-hidden="true" />
                    <span><strong>Medarbejdere:</strong> {COMPANY_INFO.employeesCount}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-500 text-xs" aria-hidden="true" />
                    <span><strong>Lokation:</strong> {COMPANY_INFO.address}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-emerald-500 text-xs" aria-hidden="true" />
                    <span><strong>Motto:</strong> {COMPANY_INFO.slogan}</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>

          {/* Gallery block */}
          {galleryImages && galleryImages.length > 0 && (
            <div className="mt-16 pt-12 border-t border-slate-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                Billeder
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {galleryImages.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setActiveImageIndex(idx)}
                    className="group relative rounded-2xl overflow-hidden bg-slate-100 aspect-[4/3] shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    <img
                      src={imgUrl}
                      alt={`Hvem er vi billede ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <i className="fa-solid fa-magnifying-glass-plus text-xl" aria-hidden="true" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Form Section */}
      <ContactFormSection />

      {/* Lightbox Modal */}
      <LightboxModal
        images={galleryImages}
        activeIndex={activeImageIndex}
        onClose={() => setActiveImageIndex(null)}
        onPrev={() => setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setActiveImageIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />
    </>
  );
}
